import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Radar, TrendingUp } from "lucide-react";

export default function ChannelPerformance() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(14);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/events/channels?days=${days}`);
      setData(data);
    })();
  }, [days]);

  return (
    <div className="mt-10 rounded-xl border border-[#27272A] bg-[#121212] p-6" data-testid="channel-performance-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Radar className="h-4 w-4 text-[#007AFF]" />
          <h2 className="font-black text-xl tracking-tight">Channel performance</h2>
        </div>
        <div className="flex gap-1">
          {[7, 14, 30].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`channel-days-${d}`}
              className={`px-3 py-1 rounded-full text-xs border ${days === d ? "bg-white text-black border-white" : "text-neutral-400 border-white/15 hover:bg-white/5"}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>
      <p className="mt-1 text-xs text-neutral-500">
        Which ad channel converts cheapest — pour the budget into the top row. Tracked via UTM links.
      </p>

      {data && (
        <>
          <div className="mt-4 flex flex-wrap gap-6 text-sm">
            <Kpi label="Visitors" value={data.totals.visitors} />
            <Kpi label="Signups" value={data.totals.signups} accent="text-[#34C759]" />
            <Kpi label="Waitlist" value={data.totals.waitlist_joins} />
            <Kpi label="Conversion" value={`${data.totals.conversion_pct}%`} accent="text-[#007AFF]" />
          </div>

          {data.rows.length === 0 ? (
            <div className="mt-5 text-sm text-neutral-500" data-testid="channel-empty">
              No tracked traffic yet — share links with <span className="font-mono text-neutral-400">?utm_source=…</span> and rows appear here.
            </div>
          ) : (
            <div className="mt-5 overflow-x-auto">
              <table className="w-full text-sm" data-testid="channel-table">
                <thead className="text-[10px] uppercase tracking-widest text-neutral-500">
                  <tr>
                    <th className="text-left py-2 pr-3">Source</th>
                    <th className="text-left py-2 pr-3">Campaign / content</th>
                    <th className="text-right py-2 pr-3">Visitors</th>
                    <th className="text-right py-2 pr-3">Views</th>
                    <th className="text-right py-2 pr-3">Signups</th>
                    <th className="text-right py-2 pr-3">Waitlist</th>
                    <th className="text-right py-2">Conv.</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r, i) => (
                    <tr key={i} className={`border-t border-[#27272A] ${i === 0 && r.signups > 0 ? "bg-[#34C759]/[0.05]" : ""}`}>
                      <td className="py-2.5 pr-3 font-semibold text-white">
                        {i === 0 && r.signups > 0 && <TrendingUp className="inline h-3.5 w-3.5 text-[#34C759] mr-1.5" />}
                        {r.source}
                      </td>
                      <td className="py-2.5 pr-3 text-neutral-500 text-xs">
                        {[r.campaign, r.content].filter(Boolean).join(" · ") || "—"}
                      </td>
                      <td className="py-2.5 pr-3 text-right font-mono">{r.visitors}</td>
                      <td className="py-2.5 pr-3 text-right font-mono text-neutral-400">{r.pageviews}</td>
                      <td className="py-2.5 pr-3 text-right font-mono text-[#34C759]">{r.signups}</td>
                      <td className="py-2.5 pr-3 text-right font-mono text-neutral-400">{r.waitlist_joins}</td>
                      <td className="py-2.5 text-right font-mono text-[#007AFF]">{r.conversion_pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className={`font-mono tabular-nums font-bold text-lg ${accent || "text-white"}`}>{value}</div>
    </div>
  );
}
